/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W6.5 (F3) — FAN MASTER-LIST OPTION RESOLVER.
 *
 * The fan selector's master-list selects (Controller / Supplied-by / Guards /
 * AFS / Mounting-method / Power / shaft-grounding) do NOT resolve their labels
 * from a CMS doc — they come back as NESTED master rows from the
 * `/master/.../fan_mfg_series/<field>/` (+ `/mtr_type/speed_condition/`) GETs.
 * Each row wraps the real option under a per-field sub-object whose `.id` is the
 * SELECT VALUE and whose `.name`/`.model` is the OPTION TEXT.
 *
 * VERBATIM from fan.selector.js renderSelectsAfterDependencyChange (5854-5932,
 * the `selectboxesWithStructuralData` switch) + mergeIdsWithCMS forSavingState
 * resolver (1874 `el[fieldsShortNames[key]]?.id || el.id || el.power`):
 *
 *   field                         nested-key            value           label
 *   ------------------------------------------------------------------------
 *   mtr_controller_id             mtr_controller        .id             .name|.model
 *   mtr_supplied_by_id            (row)                 mtr_supplied_by.id    .name
 *   mtr_base_supplied_by_id       mtr_base_supplied_by  .id             .name|.model
 *   mounting_method_id            spring_iso_mounting   .id             .name|.model
 *   fan_isolators_supplied_by_id  (row)                 fan_iso_supplied_by.id .name
 *   inlet_guard                   in_guard              .id             .name|.model
 *   outlet_guard                  out_guard             .id             .name|.model
 *   protective_cage               protect_cage          .id             .name|.model
 *   fan_afs_sensor                afs_sensor            .id             .name|.model
 *   fan_afs_transmitter           (row)                 afs_xmitter.id  afs_xmitter.model
 *   motor_shaft_grounding_id      mtr_shaft_grounding   .id             .name|.model
 *   finish_id                     finish                .id             .name|.model
 *   motor_power                   (row)                 .power          .power
 *   default                       (row)                 .id|.value      .name|.model
 *
 * ★ F3 USE: this powers BOTH the option list AND the saved-value fallback. When
 * the dependency cascade narrows the list and the saved value isn't present, the
 * fallback option's LABEL is resolved from the FULL (unfiltered) master list via
 * `fanMasterSelectedLabel` — read-only, ON OPEN, before any armed parent change.
 * The narrowing+re-pick (applyNewValues) is untouched; this only adds a render
 * option so a stored value never blanks.
 */

import type { OptionRow } from '../formKit';

/** Field → its nested short-name sub-object (legacy fieldsShortNames). When the
 * field is absent here the row's own `id`/`value`/`name`/`model` is used. */
const NESTED_KEY: Record<string, string> = {
  mtr_controller_id: 'mtr_controller',
  mtr_base_supplied_by_id: 'mtr_base_supplied_by',
  mounting_method_id: 'spring_iso_mounting',
  inlet_guard: 'in_guard',
  outlet_guard: 'out_guard',
  protective_cage: 'protect_cage',
  fan_afs_sensor: 'afs_sensor',
  motor_shaft_grounding_id: 'mtr_shaft_grounding',
  // Options-tab Finish: rows come from `fan_mfg_series/finish_id/`
  // (MasterAHUFanMfgSeriesFinish) and wrap the MasterAHUFanFinish row — its
  // `.id` is the select value the rate envelope carries as `finish_id`.
  finish_id: 'finish',
};

/** Fields whose VALUE comes from a nested `.id` but the row itself carries the
 * sub-object directly (supplied-by + afs transmitter) — legacy 5903-5924. */
const SUPPLIED_BY: Record<string, string> = {
  mtr_supplied_by_id: 'mtr_supplied_by',
  fan_isolators_supplied_by_id: 'fan_iso_supplied_by',
  fan_afs_transmitter: 'afs_xmitter',
};

/** Is this a fan MASTER-list field (vs a CMS-doc field)? */
export function isFanMasterField(field: string): boolean {
  return (
    field in NESTED_KEY ||
    field in SUPPLIED_BY ||
    field === 'motor_power' ||
    field === 'fan_isolators_supplied_by_id' ||
    field === 'mtr_controller_id' ||
    field === 'mounting_method_id'
  );
}

/**
 * Map one raw master row → an OptionRow {id (the select value), label (the
 * option text)} per the legacy structural switch. Returns null when the row has
 * no resolvable option (legacy `if (optName) …` guard).
 */
export function fanMasterOption(field: string, row: any): OptionRow | null {
  if (row == null || typeof row !== 'object') return null;

  // motor_power: rows are { power: <n> } — value AND label are the power.
  if (field === 'motor_power') {
    const p = row.power ?? row.value ?? row.id;
    if (p == null) return null;
    return { ...row, id: p, label: String(p) };
  }

  // supplied-by family + afs transmitter: value/label from a nested sub-object.
  const sb = SUPPLIED_BY[field];
  if (sb) {
    const sub = row[sb];
    if (!sub) return null;
    const id = sub.id ?? sub.value;
    const label = sub.name ?? sub.model;
    if (id == null || label == null || label === '') return null;
    return { ...row, id, label: String(label) };
  }

  // the rest: value/label from the nested short-name sub-object if present,
  // else the row's own id/value + name/model.
  const nk = NESTED_KEY[field];
  const data = nk ? row[nk] : row;
  if (!data) return null;
  const id = data.id ?? data.value;
  const label = data.name ?? data.model ?? data.value;
  if (id == null) return null;
  if (label == null || label === '') return null;
  return { ...row, id, label: String(label) };
}

/**
 * ★ BUG 5 — resolve the VENDOR-RATE CODE (legacy `option.data('value')`) for a
 * saved id from a master list, mirroring fan.selector.js:5902 `dataValueID`:
 *   inlet/outlet/cage/afs_sensor → nested sub-object `.value` (e.g. in_guard
 *     id 2 → "inlet_guard_hpa"), falling back to the nested `.id`.
 *   fan_afs_transmitter          → afs_xmitter.configuration, falling back to id.
 *   default                      → row `.value` || `.id`.
 * collectData sends THIS (not the raw select id) for these search-field selects;
 * the React port leaked the id, so the vendor rejected e.g.
 * "Invalid input value 2 for parameter InletGuard". Returns undefined when the
 * id isn't in the list (the caller then keeps the raw state value, matching the
 * legacy `option.data('value') ?? elem.val()` fallback). */
export function fanMasterOptionValue(
  field: string,
  id: number | string | null | undefined,
  rows: any[] | undefined
): string | number | undefined {
  if (id == null || id === '' || !Array.isArray(rows)) return undefined;
  const sb = SUPPLIED_BY[field];
  const nk = NESTED_KEY[field];
  for (const row of rows) {
    if (row == null || typeof row !== 'object') continue;
    // the sub-object the legacy switch reads as `modelData`.
    let data: any;
    if (field === 'fan_afs_transmitter') data = row.afs_xmitter;
    else if (sb) data = row[sb];
    else if (nk) data = row[nk];
    else data = row;
    if (!data) continue;
    const optId = data.id ?? data.value;
    if (optId == null || String(optId) !== String(id)) continue;
    // legacy dataValueID: afs_xmitter.configuration → .value → .id.
    const code = (field === 'fan_afs_transmitter' ? data.configuration : undefined)
      ?? data.value ?? data.id;
    return code == null ? undefined : (code as string | number);
  }
  return undefined;
}

/** Map a raw master list → its OptionRow[] (value/label resolved), dropping
 * unresolvable rows + de-duplicating by the resolved value (legacy renders one
 * <option> per resolvable row; nested ids can repeat across rows). */
export function fanMasterOptionList(field: string, rows: any[] | undefined): OptionRow[] {
  if (!Array.isArray(rows)) return [];
  const out: OptionRow[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const opt = fanMasterOption(field, row);
    if (!opt) continue;
    const key = String(opt.id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(opt);
  }
  return out;
}

/** Resolve the DISPLAY LABEL of a saved id from a (full) master list — the F3
 * fallback-option text. Returns '' when not found (caller falls back to the id). */
export function fanMasterSelectedLabel(
  field: string,
  id: number | string | null | undefined,
  rows: any[] | undefined
): string {
  if (id == null || id === '') return '';
  const list = fanMasterOptionList(field, rows);
  // motor_power compares numerically (saved "25.00" vs option 25.0) — the rest
  // are ids and compare by string (legacy looseEq).
  const eq =
    field === 'motor_power'
      ? (a: unknown, b: unknown) => Number(a) === Number(b)
      : (a: unknown, b: unknown) => String(a) === String(b);
  const hit = list.find((r) => eq(r.id, id));
  return hit ? String(hit.label ?? '') : '';
}
